import React, { useEffect, useRef } from 'react';
import { CheckCircleOutlined, InfoOutlined } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import Grid from '@mui/material/Grid';
import Tooltip from '@mui/material/Tooltip';
import { useAutoTranslation } from 'src/hooks/useTranslate';
import {
  Box,
  Checkbox,
  Chip,
  FormControlLabel,
  FormGroup,
  Modal,
  TextField
} from '@mui/material';
import { PEDIGREE } from 'src/components/Constants';
import * as d3 from 'd3';
import axios from 'src/axios';
import { APICONSTANTS } from 'src/components/Constants';
import ModalButtons from 'src/components/UI/button/ModalButtons';
import CustomDialog from 'src/components/UI/dialog/CustomDialog';
import { AppContext, SnackContext, ThemeContext } from 'src/store/ContextStore';
import { CONSTANTS as c } from 'src/components/questionnaire/Constants';
import { EVENTS } from 'src/components/admin/appReducer';
import { getPersonAge } from 'src/util/util';
import {
  deselectAllOutlines,
  distance,
  getNodeFromIndex,
  selectOutline
} from '../util';

const Container = styled(Grid)({
  display: 'flex',
  gap: 15,
  flexWrap: 'wrap'
});

const DiseaseContainer = styled('div')({
  display: 'flex',
  flexDirection: 'row'
});

const BoxHolder = styled('div')({
  display: 'flex',
  alignItems: 'center',
  marginRight: 10,

  fontSize: '1rem',
  cursor: 'grab',
  '&:active': {
    cursor: 'grabbing'
  },
  '&.dragging': {
    opacity: 0.5
  }
});

const Item = styled('span')({
  display: 'inline-block',
  flexShrink: 0,
  width: 15,
  height: 15,
  borderRadius: 2,
  marginRight: 8
});

const ConfirmedText = styled('span')({
  fontStyle: 'normal',
  fontWeight: 'bold',
  fontSize: '0.75rem',
  color: 'black'
});

export default function LegendMenu({
  diagnoses,
  id,
  labelsSet,
  setLabelsSet,
  showLegendTxt,
  dataset,
  setDataset,
  setPedigreeState,
  draggable,
  showCheckboxes = false,
  showMasterCheckbox = true,
  carrierPresentStates = {},
  showCarrierMarks = true,
  carrierStyle = 'border'
}) {
  const t = useAutoTranslation();

  const [diseasesCases, setDiseasesCases] = React.useState({});
  const [draggedItem, setDraggedItem] = React.useState(null);
  const [modalOpen, setModalOpen] = React.useState(false);
  const [currentDiagnosis, setCurrentDiagnosis] = React.useState('none');
  const [diagnosisAge, setDiagnosisAge] = React.useState('');
  const [diagnosisComment, setDiagnosisComment] = React.useState('');
  const [loading, setLoading] = React.useState(false);

  const { setSnack } = React.useContext(SnackContext);
  const { theme } = React.useContext(ThemeContext);
  const { content, dispatchUpdate } = React.useContext(AppContext);
  const { pedigreeSelectedUsers: users } = content;
  const { selectedUser: user } = users;
  const [selectedNode, setSelectedNode] = React.useState(null);

  const selectRadius = 50;

  const chosenRef = useRef(-1);
  const lastDragPosRef = useRef({ x: null, y: null });
  const dragImgRef = useRef(null);
  const membersRef = useRef([]);
  const prevChoiceRef = useRef({ index: -1, distBucket: null });

  React.useMemo(() => {
    const patients = dataset?.filter((d) => d.diagnoses?.length > 0) || [];
    const dg = {};
    patients.forEach((member) => {
      member.diagnoses?.forEach(
        (d) => (dg[d.id] = dg[d.id] === undefined ? 1 : dg[d.id] + 1)
      );
    });
    setDiseasesCases(dg);
  }, [dataset, diagnoses]);

  const handleChange = (e) => {
    setLabelsSet((prev) => ({
      ...prev,
      [e.target.name]: e.target.checked,
      diseasesMap: Object.keys(prev.diseasesMap).reduce((acc, key) => {
        acc[key] = e.target.checked;
        return acc;
      }, {})
    }));
  };

  const updateData = (dataset) => {
    // setPedigreeState(CONSTANTS.pedigreeState.modified);
    setDataset(dataset);
  };

  const getNodeCenterClient = (el) => {
    if (!el) return null;
    const rect = el.getBoundingClientRect?.();
    if (rect && rect.width >= 1 && rect.height >= 1) {
      return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
    }
    if (el.getBBox && el.getScreenCTM) {
      const bbox = el.getBBox();
      const ctm = el.getScreenCTM();
      const cx = bbox.x + bbox.width / 2;
      const cy = bbox.y + bbox.height / 2;
      const sx = ctm.a * cx + ctm.c * cy + ctm.e;
      const sy = ctm.b * cx + ctm.d * cy + ctm.f;
      return { x: sx, y: sy };
    }
    const ctm = el.getScreenCTM?.();
    if (ctm) return { x: ctm.e, y: ctm.f };
    return null;
  };

  const highlightNearest = (mousePos) => {
    if (!mousePos || mousePos.x == null || mousePos.y == null) {
      return { index: -1, dist: Infinity, el: null };
    }

    if (!membersRef.current || membersRef.current.length === 0) {
      membersRef.current = Array.from(document.querySelectorAll('.node'));
    }
    let choice = { index: -1, dist: Infinity, el: null };

    for (let i = 0; i < membersRef.current.length; i++) {
      const el = membersRef.current[i];
      const center = getNodeCenterClient(el);
      if (!center) continue;
      const d = distance(mousePos, center);
      if (d < choice.dist) choice = { index: i, dist: d, el };
    }

    const threshold = selectRadius;
    const distBucket = Math.round(choice.dist / 5);
    if (
      choice.index !== prevChoiceRef.current.index ||
      distBucket !== prevChoiceRef.current.distBucket
    ) {
      prevChoiceRef.current = { index: choice.index, distBucket };
    }

    deselectAllOutlines();
    if (choice.index !== -1 && choice.dist < threshold) {
      selectOutline(choice.index, theme);
    }
    return choice;
  };

  const isGeneId = (id) => id?.startsWith('NCBIGene:');
  const isPhenotypeId = (id) => id?.startsWith('HP:') && id !== 'HP:999999';

  const handleSubmit = async (
    index,
    diagnosis,
    age,
    e,
    nodeOverride = null
  ) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    const node = nodeOverride || selectedNode || getNodeFromIndex(index);

    const userId = user?._id ?? user?.user_id ?? user?.id ?? null;
    const familyId = user?.familyId ?? user?.family?.id ?? null;

    const proBandId = node.proBandId;
    const familyMemberId = proBandId ? proBandId : node.familyMemberId;

    const isGene = isGeneId(diagnosis?.id);
    const isPhenotype = isPhenotypeId(diagnosis?.id);
    const isDisease = !isGene && !isPhenotype;

    if (isDisease) {
      const personAge = getPersonAge(node);

      if (personAge !== null && Number(age) > personAge) {
        setSnack({
          status: true,
          msg: `Age at diagnosis cannot be greater than the person's age (${personAge}).`,
          severity: 'error'
        });
        return;
      }
    }

    if (!diagnosis) {
      return setSnack({
        status: true,
        msg: 'No item selected.',
        severity: 'error'
      });
    }

    setLoading(true);

    try {
      if (isGene) {
        const payload = {
          familyId,
          familyMemberId,
          user_id: userId,
          isProband: !!proBandId,
          genotypes: [{ id: diagnosis.id, name: diagnosis.name }]
        };
        const res = await axios.post(APICONSTANTS.genotypes, payload);
        if (res.data?.success) {
          dispatchUpdate({
            type: EVENTS.SET_PEDIGREE_GENOTYPES,
            value: res.data.genotypes
          });
          updateData(
            dataset.map((elem) =>
              elem.pid === node.pid
                ? { ...elem, genotypes: res.data.genotypes }
                : elem
            )
          );
          setSnack({
            status: true,
            msg: 'Genotype assigned successfully',
            severity: 'success'
          });
        }
      } else if (isPhenotype) {
        const payload = {
          familyId,
          familyMemberId,
          user_id: userId,
          isProband: !!proBandId,
          phenotypes: [{ id: diagnosis.id, name: diagnosis.name }]
        };
        const res = await axios.post(APICONSTANTS.phenotypes, payload);
        if (res.data?.success) {
          dispatchUpdate({
            type: EVENTS.SET_PEDIGREE_PHENOTYPES,
            value: res.data.phenotypes
          });
          updateData(
            dataset.map((elem) =>
              elem.pid === node.pid
                ? { ...elem, phenotypes: res.data.phenotypes }
                : elem
            )
          );
          setSnack({
            status: true,
            msg: 'Phenotype assigned successfully',
            severity: 'success'
          });
        }
      } else {
        const payload = {
          familyId,
          familyMemberId,
          user_id: userId,
          isProband: !!proBandId,
          diagnoses: [{ ...diagnosis, age }]
        };

        const res = await axios.post(APICONSTANTS.diagnoses, payload);

        if (res.data?.success && res.data?.allDiagnoses) {
          dispatchUpdate({
            type: EVENTS.SET_PEDIGREE_DIAGNOSES,
            value: res.data.diagnoses
          });
          updateData(
            dataset.map((elem) =>
              elem.pid === node.pid
                ? { ...elem, diagnoses: res.data.allDiagnoses }
                : elem
            )
          );
          setSnack({
            status: true,
            msg: 'Diagnosis assigned successfully',
            severity: 'success'
          });
        }
      }
    } catch (err) {
      console.clear();
      console.log(err);
      setSnack({
        status: true,
        msg: 'An error has occurred!',
        severity: 'error'
      });
    } finally {
      setLoading(false);
      setModalOpen(false);
      setDiagnosisAge('');
      setDiagnosisComment('');
      deselectAllOutlines();
    }
  };

  useEffect(() => {
    if (!draggedItem) return;

    const onDragOver = (e) => {
      e.preventDefault();
      const x = e.clientX ?? e.pageX ?? null;
      const y = e.clientY ?? e.pageY ?? null;
      lastDragPosRef.current = { x, y };
      highlightNearest(lastDragPosRef.current);
    };

    document.addEventListener('dragover', onDragOver, { passive: false });

    return () => {
      document.removeEventListener('dragover', onDragOver);
    };
  }, [draggedItem, theme]);

  const handleDragStart = (e, diagnosis) => {
    setDraggedItem(diagnosis);
    setCurrentDiagnosis(diagnosis);
    membersRef.current = Array.from(document.querySelectorAll('.node'));

    e.dataTransfer.setData('text/plain', diagnosis.name || 'diagnosis');
    e.dataTransfer.setData(
      'application/json',
      JSON.stringify({
        id: diagnosis.id,
        name: diagnosis.name,
        color: diagnosis.color,
        cases: diseasesCases[diagnosis.id] || 0,
        fullDiagnosis: diagnosis
      })
    );

    const dragChip = document.createElement('div');
    dragChip.className = 'drag-chip';
    dragChip.innerHTML = `
      <div style="
        background: ${diagnosis.color || '#1c1c1dff'};
        color: white;
        padding: 8px 16px;
        border-radius: 20px;
        width:max-content;
      ">
        ${diagnosis.name}
      </div>`;

    document.body.appendChild(dragChip);
    dragImgRef.current = dragChip;
    e.dataTransfer.setDragImage(dragChip, 60, 20);

    const coords = {
      clientX: e.clientX,
      clientY: e.clientY,
      pageX: e.pageX,
      pageY: e.pageY
    };

    lastDragPosRef.current = { x: e.clientX ?? null, y: e.clientY ?? null };

    setTimeout(() => {
      if (document.body.contains(dragChip)) {
        document.body.removeChild(dragChip);
      }
    }, 0);
  };

  const handleDrag = (e) => {
    if (!draggedItem) return;
    const x = e.clientX ?? e.pageX ?? lastDragPosRef.current.x;
    const y = e.clientY ?? e.pageY ?? lastDragPosRef.current.y;
    lastDragPosRef.current = { x, y };
    highlightNearest(lastDragPosRef.current);
  };

  const handleDragEnd = (e) => {
    if (dragImgRef.current && document.body.contains(dragImgRef.current)) {
      document.body.removeChild(dragImgRef.current);
      dragImgRef.current = null;
    }

    const raw = {
      x: e.clientX ?? e.pageX ?? null,
      y: e.clientY ?? e.pageY ?? null
    };
    const last = lastDragPosRef.current;
    let usePos = last && last.x != null && last.y != null ? last : raw;

    if (last && raw && raw.x != null && raw.y != null) {
      const jump = Math.hypot(raw.x - last.x, raw.y - last.y);
      if (jump > 100) {
        usePos = last;
      }
    }

    const choice = highlightNearest(usePos || { x: null, y: null });
    deselectAllOutlines();

    if (choice.index !== -1 && choice.dist < selectRadius && draggedItem) {
      let nodeOverride = null;
      const el = choice.el;
      if (el) {
        const pidFromDom =
          el.dataset?.pid ||
          el.getAttribute?.('data-pid') ||
          el.getAttribute?.('pid') ||
          el.id ||
          null;
        if (pidFromDom && Array.isArray(dataset)) {
          nodeOverride = dataset.find(
            (n) => String(n.pid) === String(pidFromDom)
          );
        }
      }

      if (
        !isGeneId(draggedItem?.id) &&
        !isPhenotypeId(draggedItem?.id) &&
        draggedItem !== 'other' &&
        !c?.pregnanciesDiagnoses?.includes(draggedItem.name)
      ) {
        chosenRef.current = choice.index;

        let nodeOverride = null;
        const el = choice.el;
        if (el) {
          const pidFromDom =
            el.dataset?.pid ||
            el.getAttribute?.('data-pid') ||
            el.getAttribute?.('pid') ||
            el.id ||
            null;
          if (pidFromDom && Array.isArray(dataset)) {
            nodeOverride = dataset.find(
              (n) => String(n.pid) === String(pidFromDom)
            );
          }
        }
        setSelectedNode(nodeOverride);

        setModalOpen(true);
      } else {
        // Direct submit for diagnoses that don't need age
        handleSubmit(
          choice.index,
          draggedItem,
          diagnosisAge,
          undefined,
          nodeOverride
        );
      }
    }

    setDraggedItem(null);
    membersRef.current = [];
  };

  const handleCloseModal = () => {
    deselectAllOutlines();
    setModalOpen(false);
    setDiagnosisAge('');
  };

  const labels = {
    diseases: 'Diseases'
  };

  const isClinicallyConfirmed = diagnoses.some(
    (diagnose) => diagnose.clinicallyConfirmed
  );

  const handleDiseaseToggle = (id) => {
    setLabelsSet((prev) => ({
      ...prev,
      diseasesMap: {
        ...prev.diseasesMap,
        [id]: !prev.diseasesMap[id]
      }
    }));
  };

  return (
    <>
      <Grid>
        {labelsSet && showCheckboxes && showMasterCheckbox && (
          <FormGroup>
            {Object.keys(PEDIGREE.legendLabelFilters).map((label) => {
              return (
                <FormControlLabel
                  key={label}
                  control={
                    <Checkbox
                      checked={labelsSet[label]}
                      onChange={handleChange}
                      size="small"
                      name={label}
                    />
                  }
                  label={t(labels[label])}
                />
              );
            })}
          </FormGroup>
        )}
        {(!labelsSet || labelsSet?.diseases) && (
          <Container container id={id} style={{ gap: showCheckboxes ? 4 : 15 }}>
            {diagnoses
              .filter(
                (el) =>
                  showCheckboxes ||
                  !labelsSet?.diseasesMap ||
                  labelsSet.diseasesMap[el.id] !== false
              )
              .map((el, i) => {
                const isDragging = draggedItem?.id === el.id;
                return (
                  <DiseaseContainer key={i}>
                    {labelsSet && showCheckboxes && (
                      <Checkbox
                        checked={labelsSet?.diseasesMap?.[el.id]}
                        onChange={() => handleDiseaseToggle(el.id)}
                        size="small"
                      />
                    )}

                    <BoxHolder
                      draggable={draggable}
                      onDragStart={(e) => handleDragStart(e, el)}
                      onDrag={handleDrag}
                      onDragEnd={handleDragEnd}
                      className={isDragging ? 'dragging' : ''}
                      title={`Drag ${el.name} to assign to a family member`}
                    >
                      <Item
                        style={{
                          backgroundColor: el.color ?? 'black'
                        }}
                      />
                      <span>
                        {t(el.name)}
                        {el.id ? (
                          <span
                            style={{ fontSize: '0.85rem', color: '#757575' }}
                          >
                            {` (${el.id})`}
                          </span>
                        ) : (
                          ''
                        )}
                        <Chip
                          label={`${diseasesCases[el.id] || 0} cases`}
                          size={'small'}
                          sx={{
                            fontStyle: 'italic',
                            fontSize: 11,
                            mx: 1,
                            height: 1
                          }}
                        />
                      </span>
                    </BoxHolder>
                  </DiseaseContainer>
                );
              })}
            {showCarrierMarks &&
              diagnoses.some(
                (el) =>
                  (!labelsSet?.diseasesMap ||
                    labelsSet.diseasesMap[el.id] !== false) &&
                  carrierPresentStates?.[el.id]?.has?.('carrier')
              ) && (
                <div style={{ width: '100%', marginTop: 10 }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                      marginBottom: 4
                    }}
                  >
                    <ConfirmedText style={{ color: theme?.header }}>
                      {t('Carriers')}
                    </ConfirmedText>
                    <Tooltip
                      title={t(
                        carrierStyle === 'verticalLine'
                          ? "Carriers are shown by a vertical line through the symbol in the disease's colour, following Bennett et al. 2022 pedigree nomenclature. This style is used when a single disease is filtered on the pedigree."
                          : "Carriers are shown as a bold coloured outline on the relevant disease segment, in the disease's colour. This is a TrakGene-specific convention that extends standard pedigree notation to multi-disease pedigrees."
                      )}
                      enterTouchDelay={0}
                      leaveTouchDelay={4000}
                      arrow
                    >
                      <InfoOutlined
                        sx={{ fontSize: 15, opacity: 0.7, cursor: 'pointer' }}
                      />
                    </Tooltip>
                  </div>
                  {diagnoses
                    .filter(
                      (el) =>
                        (!labelsSet?.diseasesMap ||
                          labelsSet.diseasesMap[el.id] !== false) &&
                        carrierPresentStates?.[el.id]?.has?.('carrier')
                    )
                    .map((el, i) => (
                      <DiseaseContainer
                        key={`carrier-${el.id ?? i}`}
                        style={{ alignItems: 'center', marginBottom: 8 }}
                      >
                        {carrierStyle === 'verticalLine' ? (
                          <Item
                            style={{
                              backgroundColor: 'transparent',
                              border: '1px solid #bdbdbd',
                              position: 'relative',
                              overflow: 'visible'
                            }}
                          >
                            <span
                              style={{
                                position: 'absolute',
                                left: '50%',
                                top: -4,
                                bottom: 0,
                                width: 2,
                                transform: 'translateX(-50%)',
                                backgroundColor: el.color ?? 'black'
                              }}
                            />
                          </Item>
                        ) : (
                          <Item
                            style={{
                              backgroundColor: 'transparent',
                              border: `2.5px solid ${el.color ?? 'black'}`
                            }}
                          />
                        )}
                        <span style={{ fontSize: '0.85rem' }}>
                          {t(el.name)}
                          {el.id ? (
                            <span style={{ color: '#757575' }}>
                              {` (${el.id})`}
                            </span>
                          ) : (
                            ''
                          )}
                        </span>
                      </DiseaseContainer>
                    ))}
                  <div
                    style={{
                      fontSize: 11,
                      fontStyle: 'italic',
                      opacity: 0.8,
                      marginTop: 4
                    }}
                  >
                    {t(
                      "Tested carriers have a variant label; other carriers don't."
                    )}
                  </div>
                </div>
              )}
            {showLegendTxt && isClinicallyConfirmed && !showCheckboxes && (
              <div style={{ width: '100%' }}>
                <hr
                  style={{
                    border: 'none',
                    borderTop: '1px solid currentColor',
                    opacity: 0.4,
                    margin: '6px 0'
                  }}
                />
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  {labelsSet && showCheckboxes && (
                    <span
                      style={{
                        display: 'inline-block',
                        width: 42,
                        flexShrink: 0
                      }}
                    />
                  )}
                  <BoxHolder style={{ cursor: 'default' }}>
                    <svg
                      width="15"
                      height="15"
                      style={{ flexShrink: 0, marginRight: 8 }}
                      aria-hidden="true"
                    >
                      <defs>
                        <pattern
                          id="legend-dx-confirmed-stripe"
                          x="0"
                          y="0"
                          width="8"
                          height="8"
                          patternUnits="userSpaceOnUse"
                          patternTransform="rotate(45)"
                        >
                          <rect
                            x="0"
                            y="0"
                            width="4"
                            height="8"
                            fill="white"
                            fillOpacity="0.55"
                          />
                        </pattern>
                      </defs>
                      <rect
                        x="0"
                        y="0"
                        width="15"
                        height="15"
                        rx="2"
                        fill="#9e9e9e"
                      />
                      <rect
                        x="0"
                        y="0"
                        width="15"
                        height="15"
                        rx="2"
                        fill="url(#legend-dx-confirmed-stripe)"
                      />
                    </svg>
                    <span style={{ fontSize: '0.75rem', fontWeight: 'bold' }}>
                      {t('Clinically confirmed diagnosis')}
                    </span>
                    <CheckCircleOutlined
                      sx={{ fontSize: 16, color: '#2e7d32', ml: 0.5 }}
                    />
                  </BoxHolder>
                </div>
              </div>
            )}
          </Container>
        )}
      </Grid>
      <Modal open={modalOpen} onClose={handleCloseModal}>
        <CustomDialog
          maxWidth={'sm'}
          title={`Age ${currentDiagnosis?.name || ''} Diagnosed`}
          open={modalOpen}
          handleClose={handleCloseModal}
          minHeight={'auto'}
          onClose={true}
        >
          <form className="user-form">
            <Grid container spacing={2}>
              <Grid size={12}>
                <TextField
                  variant="outlined"
                  fullWidth
                  label={`Age at ${currentDiagnosis?.name || ''} diagnosis`}
                  onChange={(e) => setDiagnosisAge(e.target.value)}
                  name={currentDiagnosis?.name || ''}
                  value={diagnosisAge}
                  placeholder="Enter age"
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  variant="outlined"
                  fullWidth
                  multiline
                  rows={3}
                  label="Diagnosis Comment"
                  onChange={(e) => setDiagnosisComment(e.target.value)}
                  value={diagnosisComment}
                  placeholder="Enter additional details or comments..."
                />
              </Grid>
              <ModalButtons
                hideContinueAdding={true}
                handleClose={handleCloseModal}
                onClick={async (e) =>
                  handleSubmit(
                    chosenRef.current,
                    currentDiagnosis,
                    diagnosisAge,
                    e
                  )
                }
                addLabel={`Save`}
                loading={loading}
              />
            </Grid>
          </form>
        </CustomDialog>
      </Modal>
    </>
  );
}
